import { useState, useCallback, useRef, useMemo } from 'react';
import { getSalesAnalysis } from '../../../services/geminiService';
import { Lead, CrmUser } from '../../../types';
import { useLossReasons } from '../../settings/loss-reasons/hooks/useLossReasons';
import { DateRange } from '../../../components/shared/filters/DateRangePicker';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import {
    buildChatStats,
    filterConversationsForAnalysis,
    type AnalysisConversationSummary,
} from '../lib/analysisChatStats';
import { buildLeadsSummary } from '../lib/buildLeadsSummary';
import { fetchConversationSummaries } from '../../../services/modules/aiChatContext';

const DEFAULT_ANALYSIS_ERROR =
    'Não foi possível gerar a análise. Tente novamente em alguns instantes.';

function resolveAnalysisErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        const msg = error.message.trim();
        if (msg) return msg;
    }
    return DEFAULT_ANALYSIS_ERROR;
}

interface UseAnalysisModalProps {
    leads: Lead[];
    users: CrmUser[];
    /** Opcional: se omitido, busca atendimentos no clique da análise. */
    conversations?: AnalysisConversationSummary[];
    dateRange?: DateRange;
}

export const useAnalysisModal = ({
    leads,
    users,
    conversations: conversationsProp,
    dateRange,
}: UseAnalysisModalProps) => {
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [analysisResult, setAnalysisResult] = useState('');
    const [analysisError, setAnalysisError] = useState<string | null>(null);
    const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);

    const { currentOrganization } = useOrganization();
    const { user } = useAuth();
    const { canViewAllConversations } = usePermissions();
    const { reasons: lossReasons } = useLossReasons();

    const isCancelledRef = useRef(false);

    const userNameMap = useMemo(() => {
        const map = new Map<string, string>();
        users.forEach((u) => {
            if (u.id && u.name) map.set(u.id, u.name);
        });
        return map;
    }, [users]);

    const lossReasonMap = useMemo(() => {
        const map = new Map<string, string>();
        lossReasons.forEach((r) => {
            if (r.id && r.name) map.set(r.id, r.name);
        });
        return map;
    }, [lossReasons]);

    const resolveConversations = useCallback(async (): Promise<AnalysisConversationSummary[]> => {
        if (conversationsProp && conversationsProp.length > 0) {
            return filterConversationsForAnalysis(conversationsProp, {
                dateRange,
                currentUserId: user?.id,
                canViewAllConversations,
            });
        }

        const orgId = currentOrganization?.id;
        if (!orgId) return [];

        const fetched = await fetchConversationSummaries(orgId, dateRange);
        return filterConversationsForAnalysis(fetched, {
            dateRange,
            currentUserId: user?.id,
            canViewAllConversations,
        });
    }, [
        conversationsProp,
        dateRange,
        user?.id,
        canViewAllConversations,
        currentOrganization?.id,
    ]);

    const handleAnalyze = useCallback(async () => {
        isCancelledRef.current = false;
        setIsAnalysisModalOpen(true);
        setIsLoadingAnalysis(true);
        setAnalysisError(null);
        setAnalysisResult('');

        try {
            const leadsSummary = buildLeadsSummary(leads, userNameMap, lossReasonMap);
            const conversations = await resolveConversations();
            const chatStats = buildChatStats(conversations, userNameMap);

            let dateRangeText = 'Todo o período';
            if (dateRange?.start && dateRange?.end) {
                const formatter = new Intl.DateTimeFormat('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                });
                dateRangeText = `${formatter.format(dateRange.start)} até ${formatter.format(dateRange.end)}`;
            }

            const result = await getSalesAnalysis(leadsSummary, chatStats, dateRangeText);

            if (!isCancelledRef.current) {
                setAnalysisResult(result);
            }
        } catch (error: unknown) {
            if (!isCancelledRef.current) {
                setAnalysisError(resolveAnalysisErrorMessage(error));
                setAnalysisResult('');
            }
        } finally {
            if (!isCancelledRef.current) {
                setIsLoadingAnalysis(false);
            }
        }
    }, [leads, resolveConversations, userNameMap, lossReasonMap, dateRange]);

    const closeAnalysisModal = useCallback(() => {
        isCancelledRef.current = true;
        setIsAnalysisModalOpen(false);
        setIsLoadingAnalysis(false);
        setAnalysisError(null);
    }, []);

    return {
        isAnalysisModalOpen,
        analysisResult,
        analysisError,
        isLoadingAnalysis,
        handleAnalyze,
        closeAnalysisModal,
    };
};
